import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FiArrowLeft, FiPlus, FiUsers,
    FiX, FiUserPlus, FiChevronRight, FiAlertCircle,
    FiSearch, FiCheck, FiUserMinus
} from 'react-icons/fi';
import './HrProductDetail.css';
import ProjectsList from './IT/ProjectsList';
import toast from 'react-hot-toast';
import ConfirmModal from './shared/ConfirmModal';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const STATUS_META = {
    ACTIVE: { label: 'Active', color: '#16a34a', bg: '#dcfce7' },
    INACTIVE: { label: 'Inactive', color: '#64748b', bg: '#f1f5f9' },
    PLANNING: { label: 'Planning', color: '#d97706', bg: '#fef3c7' },
    IN_PROGRESS: { label: 'In Progress', color: '#2563eb', bg: '#dbeafe' },
    COMPLETED: { label: 'Completed', color: '#059669', bg: '#d1fae5' },
    ON_HOLD: { label: 'On Hold', color: '#ea580c', bg: '#ffedd5' },
    CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fee2e2' },
    MAINTENANCE: { label: 'Maintenance', color: '#d97706', bg: '#fef3c7' },
    REVIEW: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
};

const AVATAR_COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#ec4899',
    '#8b5cf6', '#14b8a6', '#f97316',
];

// const HEALTH_COLOR = (v) => v >= 80 ? '#16a34a' : v >= 50 ? '#d97706' : '#dc2626';

const EMPTY_PROJECT = {
    projectName: '', projectCode: '', description: '', clientName: '',
    projectManagerId: '', startDate: '', endDate: '', status: 'PLANNING', budget: ''
};

function Avatar({ name, index, size = 32 }) {
    const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
    const initials = name ? name.split(/[\s_]/).map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
    return (
        <div className="pd-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
            {initials}
        </div>
    );
}

export default function HrProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [projects, setProjects] = useState([]);
    const [teamUsers, setTeamUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('projects');

    // Project modal
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);
    const [editingProject, setEditingProject] = useState(null);

    // Team assign modal
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [teamProject, setTeamProject] = useState(null);
    const [projectUsers, setProjectUsers] = useState([]);
    const [assignUsername, setAssignUsername] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [assignDropOpen, setAssignDropOpen] = useState(false);
    const [deassignConfirm, setDeassignConfirm] = useState({ open: false, username: null, loading: false });

    const fetchAll = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [projRes, teamRes] = await Promise.all([
                api.get(`/product/${productId}/projects`),
                api.get(`/product/${productId}/users`).catch(() => ({ data: [] })),
            ]);
            // Normalize to array regardless of API response shape
            const rawProj = projRes.data;
            const projList = Array.isArray(rawProj) ? rawProj
                : Array.isArray(rawProj?.content) ? rawProj.content
                : Array.isArray(rawProj?.data) ? rawProj.data
                : [];
            setProjects(projList);
            const rawTeam = teamRes.data;
            const teamList = Array.isArray(rawTeam) ? rawTeam
                : Array.isArray(rawTeam?.content) ? rawTeam.content
                : [];
            setTeamUsers(teamList);
        } catch { setError('Failed to load product data.'); }
        finally { setLoading(false); }
    }, [productId]);

    const fetchProduct = useCallback(async () => {
        // No direct GET /product/:id endpoint in the API docs,
        // so we read from sessionStorage where HrProjectManagement stored it
        const stored = sessionStorage.getItem(`product_${productId}`);
        if (stored) setProduct(JSON.parse(stored));
    }, [productId]);

    const fetchAllUsers = async () => {
        try { const r = await api.get('/admin/users'); setAllUsers(r.data); } catch { }
    };

    useEffect(() => {
        fetchProduct();
        fetchAll();
        fetchAllUsers();
    }, [fetchProduct, fetchAll]);

    // derive health
    const getHealth = (proj) => {
        if (proj.status === 'COMPLETED') return 100;
        if (proj.status === 'IN_PROGRESS') return Math.floor(40 + ((proj.id || 1) % 50));
        if (proj.status === 'PLANNING') return Math.floor(10 + ((proj.id || 1) % 20));
        if (proj.status === 'REVIEW') return Math.floor(75 + ((proj.id || 1) % 20));
        return 50;
    };

    const productHealth = () => {
        if (!projects.length) return 0;
        return Math.round(projects.reduce((sum, p) => sum + getHealth(p), 0) / projects.length);
    };

    // const formatDate = (d) => {
    //     if (!d) return null;
    //     return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    // };

    // ── Project CRUD ──
    const openAddProject = () => {
        const timestamp = Date.now().toString().slice(-5);
        setProjectForm({
            ...EMPTY_PROJECT,
            productId: parseInt(productId),
            projectCode: `PRJ-${timestamp}`
        });
        setEditingProject(null);
        setShowProjectModal(true);
    };

    // const openEditProject = (proj, e) => {
    //     e.stopPropagation();
    //     setProjectForm({ ...proj, productId: parseInt(productId) });
    //     setEditingProject(proj.id);
    //     setShowProjectModal(true);
    // };

    const saveProject = async () => {
        if (!projectForm.projectName?.trim()) { toast.error('Project name is required.'); return; }
        if (!projectForm.projectCode?.trim()) { toast.error('Project code is required.'); return; }
        try {
            const body = {
                projectName: projectForm.projectName.trim(),
                projectCode: projectForm.projectCode.trim(),
                description: projectForm.description?.trim() || null,
                clientName: projectForm.clientName?.trim() || null,
                projectManagerId: projectForm.projectManagerId?.trim() || null,
                startDate: projectForm.startDate || null,
                endDate: projectForm.endDate || null,
                status: projectForm.status || 'PLANNING',
                budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
                productId: parseInt(productId),
            };
            if (editingProject) body.id = editingProject;
            await api.post('/project/create', body);
            setShowProjectModal(false);
            fetchAll();
        } catch (e) {
            const msg = e.response?.data;
            if (typeof msg === 'string' && msg.includes('duplicate')) {
                toast.error(`Project code "${projectForm.projectCode}" already exists.`);
            } else {
                toast.error('Failed to save project. Check all required fields.');
            }
        }
    };

    const [deleteProjectConfirm, setDeleteProjectConfirm] = useState({ open: false, project: null, loading: false });

    const openDeleteProjectModal = (proj, e) => {
        if (e) e.stopPropagation();
        setDeleteProjectConfirm({ open: true, project: proj, loading: false });
    };

    const confirmDeleteProject = async () => {
        const proj = deleteProjectConfirm.project;
        if (!proj) return;
        setDeleteProjectConfirm(prev => ({ ...prev, loading: true }));
        try {
            await api.delete(`/project/${proj.id}`);
            toast.success(`Project "${proj.projectName || 'Project'}" has been deleted.`);
            setDeleteProjectConfirm({ open: false, project: null, loading: false });
            fetchAll();
        } catch {
            toast.error(`Failed to delete project "${proj.projectName || 'Project'}".`);
            setDeleteProjectConfirm(prev => ({ ...prev, loading: false }));
        }
    };

    // ── Team ──
    // const openTeam = async (proj, e) => {
    //     e.stopPropagation();
    //     setTeamProject(proj); setAssignUsername(''); setUserSearch(''); setAssignDropOpen(false);
    //     try {
    //         const res = await api.get(`/project/users/all?projectId=${proj.id}`);
    //         setProjectUsers(res.data);
    //     } catch { setProjectUsers([]); }
    //     setShowTeamModal(true);
    // };

    const assignUser = async () => {
        if (!assignUsername.trim()) return;
        try {
            await api.post('/project/assign', { projectId: teamProject.id, username: assignUsername.trim() });
            const res = await api.get(`/project/users/all?projectId=${teamProject.id}`);
            const raw = res.data;
            setProjectUsers(Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : []);
            setAssignUsername('');
            fetchAll(); // refresh team count
        } catch { toast.error('Failed to assign user.'); }
    };

    const deassignUser = async (username) => {
        setDeassignConfirm({ open: true, username, loading: false });
    };

    const confirmDeassign = async () => {
        const { username } = deassignConfirm;
        setDeassignConfirm(prev => ({ ...prev, loading: true }));
        try {
            await api.post('/project/deassign', { projectId: teamProject.id, username });
            const res = await api.get(`/project/users/all?projectId=${teamProject.id}`);
            const raw = res.data;
            setProjectUsers(Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : []);
            fetchAll();
            toast.success(`${username} removed from project.`);
        } catch { toast.error('Failed to remove user from project.'); }
        setDeassignConfirm({ open: false, username: null, loading: false });
    };

    // const ph = productHealth();
    const sm = product ? (STATUS_META[product.status] || STATUS_META.ACTIVE) : STATUS_META.ACTIVE;

    return (
        <div className="hr-content animate-fade-in">
            <ConfirmModal
                isOpen={deassignConfirm.open}
                onClose={() => setDeassignConfirm({ open: false, username: null, loading: false })}
                onConfirm={confirmDeassign}
                loading={deassignConfirm.loading}
                title="Remove Member?"
                message={`Are you sure you want to remove ${deassignConfirm.username} from this project?`}
                confirmText="Remove"
                variant="warning"
            />
            <ConfirmModal
                isOpen={deleteProjectConfirm.open}
                onClose={() => setDeleteProjectConfirm({ open: false, project: null, loading: false })}
                onConfirm={confirmDeleteProject}
                loading={deleteProjectConfirm.loading}
                title="Delete Project?"
                message={`Are you sure you want to delete "${deleteProjectConfirm.project?.projectName || 'this project'}"? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />
            <div className="ppd-page">

                {/* ── Back link ── */}
                <button className="ppd-back" onClick={() => navigate('/hr/projects')}>
                    <FiArrowLeft /> All products
                </button>

                {/* ── Product header banner ── */}
                <div className="ppd-banner">
                    <div className="ppd-banner-left">
                        <div className="ppd-banner-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32" />
                            </svg>
                        </div>
                        <div className="ppd-banner-info">
                            <div className="ppd-banner-title-row">
                                <h1 className="ppd-banner-title">{product?.productname || product?.productName || product?.name || 'Unnamed Product'}</h1>
                                <span className="ppd-status-pill" style={{ color: sm.color, background: sm.bg }}>{sm.label}</span>
                            </div>
                            {product?.description && <p className="ppd-banner-desc">{product.description}</p>}
                            <div className="ppd-banner-meta">
                                {product?.manager_id?.id && (
                                    <span>Owner <strong>{product.manager_id.id}</strong></span>
                                )}
                                {product?.clientName?.name && (
                                    <><span className="ppd-dot">·</span><span>Client <strong>{product.clientName.name}</strong></span></>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div className="ppd-tabs-bar">
                    {[
                        { id: 'projects', label: 'Projects', count: projects.length },
                        { id: 'team', label: 'Team', count: teamUsers.length },
                        { id: 'activity', label: 'Activity' },
                        { id: 'docs', label: 'Docs' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            className={`ppd-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                            {tab.count !== undefined && (
                                <span className="ppd-tab-count">{tab.count}</span>
                            )}
                        </button>
                    ))}
                    <div className="ppd-tabs-spacer" />
                    {activeTab === 'projects' && (
                        <button className="ppd-add-btn" onClick={openAddProject}>
                            <FiPlus /> Add Project
                        </button>
                    )}
                </div>

                {error && <div className="ppd-error"><FiAlertCircle /> {error}</div>}

                {/* ── Projects tab ── */}
                {activeTab === 'projects' && (
                    <div className="ppd-project-list-wrapper mt-6">
                        <ProjectsList projects={projects} loading={loading} />
                    </div>
                )}

                {/* ── Team tab ── */}
                {activeTab === 'team' && (
                    <div className="ppd-members-wrap">
                        <div className="ppd-members-header">
                            <div className="ppd-members-title">Members</div>
                            <div className="ppd-members-sub">People who can access this product</div>
                        </div>
                        {teamUsers.length === 0 ? (
                            <div className="ppd-empty" style={{ border: 'none' }}>No team members assigned yet.</div>
                        ) : (
                            <div className="ppd-members-list">
                                {teamUsers.map((u, i) => {
                                    const presence = ['Online', 'Away', 'Offline'][i % 3];
                                    const pm = { Online: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' }, Away: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' }, Offline: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' } }[presence];
                                    const roleLabel = u.role?.replace('ROLE_', '') || 'Member';
                                    return (
                                        <div key={u.id} className="ppd-member-row">
                                            <Avatar name={u.username} index={i} size={42} />
                                            <div className="ppd-member-info">
                                                <div className="ppd-member-name">{u.username}</div>
                                                <div className="ppd-member-meta">
                                                    <span>{roleLabel}</span>
                                                    <span className="ppd-member-dot">·</span>
                                                    <span>{u.email}</span>
                                                </div>
                                            </div>
                                            <span className="ppd-presence-badge" style={{ color: pm.color, background: pm.bg, borderColor: pm.border }}>{presence}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Activity / Docs placeholders ── */}
                {activeTab === 'activity' && (
                    <div className="ppd-empty" style={{ paddingTop: 60 }}>Activity feed coming soon.</div>
                )}
                {activeTab === 'docs' && (
                    <div className="ppd-empty" style={{ paddingTop: 60 }}>Documents coming soon.</div>
                )}
            </div>

            {/* ── Project Modal ── */}
            {showProjectModal && (
                <div className="ppd-modal-overlay" onClick={() => setShowProjectModal(false)}>
                    <div className="ppd-modal" onClick={e => e.stopPropagation()}>
                        <div className="ppd-modal-header">
                            <h3>{editingProject ? 'Edit Project' : 'New Project'}</h3>
                            <button onClick={() => setShowProjectModal(false)}><FiX /></button>
                        </div>
                        <div className="ppd-modal-body">
                            <div className="ppd-field-row">
                                <div className="ppd-field"><label>Project Name *</label>
                                    <input value={projectForm.projectName} onChange={e => setProjectForm({ ...projectForm, projectName: e.target.value })} /></div>
                                <div className="ppd-field"><label>Project Code *</label>
                                    <input value={projectForm.projectCode} onChange={e => setProjectForm({ ...projectForm, projectCode: e.target.value })} /></div>
                            </div>
                            <div className="ppd-field-row">
                                <div className="ppd-field"><label>Client</label>
                                    <input value={projectForm.clientName} onChange={e => setProjectForm({ ...projectForm, clientName: e.target.value })} /></div>
                                <div className="ppd-field"><label>Manager ID</label>
                                    <input value={projectForm.projectManagerId} onChange={e => setProjectForm({ ...projectForm, projectManagerId: e.target.value })} /></div>
                            </div>
                            <div className="ppd-field-row">
                                <div className="ppd-field"><label>Start Date</label>
                                    <input type="date" value={projectForm.startDate} onChange={e => setProjectForm({ ...projectForm, startDate: e.target.value })} /></div>
                                <div className="ppd-field"><label>End Date</label>
                                    <input type="date" value={projectForm.endDate} onChange={e => setProjectForm({ ...projectForm, endDate: e.target.value })} /></div>
                            </div>
                            <div className="ppd-field-row">
                                <div className="ppd-field"><label>Budget (₹)</label>
                                    <input type="number" value={projectForm.budget} onChange={e => setProjectForm({ ...projectForm, budget: e.target.value })} /></div>
                                <div className="ppd-field"><label>Status</label>
                                    <select value={projectForm.status} onChange={e => setProjectForm({ ...projectForm, status: e.target.value })}>
                                        {['PLANNING', 'IN_PROGRESS', 'REVIEW', 'COMPLETED', 'ON_HOLD', 'CANCELLED'].map(s => <option key={s}>{s}</option>)}
                                    </select></div>
                            </div>
                            <div className="ppd-field"><label>Description</label>
                                <textarea rows={3} value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} /></div>
                        </div>
                        <div className="ppd-modal-footer">
                            <button className="ppd-btn-ghost" onClick={() => setShowProjectModal(false)}>Cancel</button>
                            <button className="ppd-btn-primary" onClick={saveProject}>Save Project</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Team Assign Modal ── */}
            {showTeamModal && (
                <div className="ppd-modal-overlay" onClick={() => setShowTeamModal(false)}>
                    <div className="ppd-modal" onClick={e => e.stopPropagation()}>
                        <div className="ppd-modal-header">
                            <h3><FiUsers style={{ marginRight: 7 }} />{teamProject?.projectName} — Team</h3>
                            <button onClick={() => setShowTeamModal(false)}><FiX /></button>
                        </div>
                        <div className="ppd-modal-body">
                            <div className="ppd-assign-section">
                                <div className="ppd-custom-select-wrap" onClick={() => setAssignDropOpen(o => !o)}>
                                    <div className="ppd-custom-select-trigger">
                                        {assignUsername ? (
                                            <>
                                                <Avatar
                                                    name={assignUsername}
                                                    index={allUsers.findIndex(u => u.username === assignUsername)}
                                                    size={26}
                                                />
                                                <span className="ppd-select-chosen">{assignUsername}</span>
                                            </>
                                        ) : (
                                            <span className="ppd-select-placeholder"><FiUsers size={14} /> Select employee…</span>
                                        )}
                                        <FiChevronRight size={14} className={`ppd-select-arrow ${assignDropOpen ? 'open' : ''}`} />
                                    </div>
                                    {assignDropOpen && (
                                        <div className="ppd-custom-dropdown" onClick={e => e.stopPropagation()}>
                                            <div className="ppd-search-row">
                                                <FiSearch size={13} className="ppd-search-icon" />
                                                <input
                                                    className="ppd-search-input"
                                                    placeholder="Search employees…"
                                                    value={userSearch}
                                                    onChange={e => setUserSearch(e.target.value)}
                                                    autoFocus
                                                />
                                                {userSearch && <FiX size={13} className="ppd-search-clear" onClick={() => setUserSearch('')} />}
                                            </div>
                                            <div className="ppd-dropdown-list">
                                                {allUsers
                                                    .filter(u =>
                                                        u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
                                                        u.email?.toLowerCase().includes(userSearch.toLowerCase())
                                                    )
                                                    .map((u, idx) => (
                                                        <div
                                                            key={u.id}
                                                            className={`ppd-dropdown-item ${assignUsername === u.username ? 'selected' : ''}`}
                                                            onClick={() => { setAssignUsername(u.username); setAssignDropOpen(false); setUserSearch(''); }}
                                                        >
                                                            <Avatar name={u.username} index={idx} size={30} />
                                                            <div className="ppd-dropdown-info">
                                                                <span className="ppd-dropdown-name">{u.username}</span>
                                                                <span className="ppd-dropdown-email">{u.email}</span>
                                                            </div>
                                                            {assignUsername === u.username && (
                                                                <FiCheck size={14} className="ppd-dropdown-check" />
                                                            )}
                                                        </div>
                                                    ))}
                                                {allUsers.filter(u =>
                                                    u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
                                                    u.email?.toLowerCase().includes(userSearch.toLowerCase())
                                                ).length === 0 && (
                                                        <div className="ppd-dropdown-empty">No employees found</div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    className={`ppd-assign-confirm-btn ${assignUsername ? 'active' : ''}`}
                                    onClick={assignUser}
                                    disabled={!assignUsername}
                                >
                                    <FiUserPlus size={14} /> Assign
                                </button>
                            </div>
                            <div className="ppd-team-modal-list">
                                {projectUsers.length === 0
                                    ? <p className="ppd-empty sm">No members yet. Assign someone above.</p>
                                    : projectUsers.map((u, i) => (
                                        <div key={u.id} className="ppd-team-modal-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                            <Avatar name={u.username} index={i} size={34} />
                                            <div style={{ flex: 1 }}>
                                                <div className="ppd-team-name">{u.username}</div>
                                                <div className="ppd-team-email">{u.email}</div>
                                            </div>
                                            <span className="ppd-team-role">{u.role?.replace('ROLE_', '')}</span>
                                            <button
                                                title="Remove from project"
                                                onClick={() => deassignUser(u.username)}
                                                style={{
                                                    background: 'none', border: '1px solid #fecaca',
                                                    color: '#ef4444', borderRadius: 8, padding: '4px 8px',
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                                    gap: 4, fontSize: 12, fontWeight: 600,
                                                    transition: 'all .15s'
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.background='#fee2e2'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background='none'; }}
                                            >
                                                <FiUserMinus size={13} /> Remove
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div className="ppd-modal-footer">
                            <button className="ppd-btn-ghost" onClick={() => setShowTeamModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

